import api from "./Api";
import format from "date-fns/esm/fp/format/index.js";
import fileDownload from 'js-file-download';
import { PageResponse } from '@models/PageResponse';
import { CTe } from "model/cte/CTe";
import { CTeFilter } from "model/cte/CTeFilter";

export interface CTeCorrection {
    group: string,
    field: string,
    value: string,
    itemNumber?: number | null
}

const toRequestFilters = (filters: CTeFilter) => ({
    status: filters.status,
    initialDate: filters.initialDateObj ? format("dd/MM/yyyy", filters.initialDateObj) : null,
    finalDate: filters.finalDateObj ? format("dd/MM/yyyy", filters.finalDateObj) : null,
    minValue: filters.vlrMinCt,
    maxValue: filters.vlrMaxCt,
    docTomador: filters.docTomador,
    serie: filters.serie,
    numDoc: filters.numDoc,
    accessKeys: filters.chaveCt,
    branchesIds: filters.branchesIds,
    companyId: filters.companyId,
    transactionsIds: filters.ids
});

export default class CTeListingApi {

    fetchFilteredCTes = (filters: CTeFilter, pageIndex: Number, pageSize: Number, orderBy: string, orderDirection: "ASC" | "DESC"): Promise<PageResponse<CTe>> => {
        const requestData = {
            ...toRequestFilters(filters),
            page: pageIndex,
            size: pageSize,
            sort: `${orderBy},${orderDirection}`
        }

        return api.get('/cte', { params: requestData }).then(({ data }) => data);
    };

    exportCTes = async (filters: CTeFilter) => {
        try {
            const res = await api.get('/cte/export', { responseType: 'blob', params: toRequestFilters(filters) });

            fileDownload(res.data, res.headers.filename)
        } catch (err) {
            console.log("Failed to export CTes. ", err);
            return null;
        }
    };

    downloadXml = async (cte: CTe) => {
        try {
            const response = await api.get(`/cte/${cte.id}/download`, {
                responseType: 'blob'
            });

            if (response.status === 200) {
                fileDownload(response.data, response.headers.filename)
            }

            return response;
        } catch (err) {
            console.log("Failed to download the CTe xml file.", err);
            return null;
        }
    }

    downloadXmlBatch = async (ids: number[]) => {
        try {
            return await api.get(`/cte/download/batch`, {
                params: { ids: ids.join(',') },
                responseType: 'blob'
            });
        } catch (err) {
            console.log("Failed to download the CTe xml batch file.", err);
            return null;
        }
    }

    downloadDacte = async (cte: CTe) => {
        try {
            return await api.get(`/cte/${cte.id}/dacte`, { responseType: 'blob' });
        } catch (err) {
            console.log("Failed to download DACTE. ", err);
            return null;
        }
    }

    downloadDacteBatch = async (ids: number[]) => {
        try {
            return await api.get(`/cte/dacte/batch`, {
                params: { ids: ids.join(',') },
                responseType: 'blob'
            });
        } catch (err) {
            console.log("Failed to download the DACTE batch file.", err);
            return null;
        }
    }

    downloadDacteMerged = async (ids: number[]) => {
        try {
            return await api.get(`/cte/dacte/merged`, {
                params: { ids: ids.join(',') },
                responseType: 'blob'
            });
        } catch (err) {
            console.log("Failed to download the DACTE merged file.", err);
            return null;
        }
    }

    cancelCTe = ({ id }: CTe, justification: string): Promise<any> => {
        return api.post('/cte/cancel', { id, justification });
    }

    amendCTe = ({ id }: CTe, corrections: CTeCorrection[]): Promise<any> => {
        return api.post('/cte/amend', { id, corrections });
    }

    fetchCTeHistory = (cteId: number): Promise<any> => {
        return api.get(`/cte/${cteId}/history`);
    }

    fetchConfig = async (rootId: number | undefined, branchId: number): Promise<any> => {
        const requestData = {
            rootId,
            branchId
        }
        try {
            return api.get('/cte/config', { params: requestData } );
        } catch (err) {
            throw new Error("Ocorreu um erro ao tentar buscar as configurações.");
        }
    }

    saveConfig = async (payload : any): Promise<any> => {
        try {
            return api.post('/cte/config', payload);
        } catch (err) {
            throw new Error("Ocorreu um erro ao tentar salvar as configurações.");
        }
    }

    canSync = async (rootId: number | undefined): Promise<any> => {
        const requestData = {
            rootId
        }
        try {
            return api.get('/cte/config/canSync', { params: requestData } );
        } catch (err) {
            throw new Error("Ocorreu um erro ao tentar validar o sync.");
        }
    }
}
